import React, { useEffect, useContext, useState } from 'react';
import 'rodal/lib/rodal.css';
import Icon from '../../../components/icon/Icon';
import PropTypes from 'prop-types';
import { styled, css } from '@mui/system';
import { Modal as BaseModal } from '@mui/base/Modal';
import Fade from '@mui/material/Fade';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import LANG, { getLangWithKey } from '../../../lang';
import useDarkMode from '../../../hooks/useDarkMode';
import ThemeContext from '../../../contexts/themeContext';

export const Ralert = ({
	msgloadalert,
	msgtypeloadalert,
	msgsmtext,
	loadalert,
	closemodel,
	confirmbt,
	fullScreen = false,
}) => {
	msgtypeloadalert = msgtypeloadalert.toLowerCase();
	const { t } = useTranslation();
	const { i18n } = useTranslation();
	const currentLang = getLangWithKey(i18n.language);
	const matches = useMediaQuery('(max-width:600px)');
	let alerttype = 'Done';
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { fullScreenStatus } = useContext(ThemeContext) || {};
	const [container, setContainer] = useState(null);

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	// Get the fullscreen container when in fullscreen mode
	useEffect(() => {
		if (fullScreenStatus) {
			// Find the fullscreen element (the .app div that goes fullscreen)
			const appElement = document.querySelector('.app');
			setContainer(appElement);
		} else {
			setContainer(null);
		}
	}, [fullScreenStatus]);

	if (msgtypeloadalert === 'error') {
		alerttype = 'ErrorOutline';
	} else if (msgtypeloadalert === 'success') {
		alerttype = 'CheckCircleOutline';
	}

	let altcolor = 'primary';
	if (msgtypeloadalert === 'error') {
		altcolor = 'danger';
	} else if (msgtypeloadalert === 'success') {
		altcolor = 'primary';
	} else {
		altcolor = 'warning';
	}

	// Only use fullScreen prop for modal size, not fullScreenStatus
	// fullScreenStatus is only used to determine the container for visibility
	const effectiveFullScreen = fullScreen;

	// Debug: log when modal renders to help troubleshoot fullscreen behavior
	if (process.env.NODE_ENV !== 'production') {
		try {
			console.debug('Ralert render:', {
				effectiveFullScreen,
				loadalert,
				msgtypeloadalert,
			});
		} catch (e) {}
	}

	const contentBoxStyle = effectiveFullScreen
		? {
			  height: '100vh',
			  width: '100vw',
			  display: 'flex',
			  flexDirection: 'column',
			  backgroundColor: darkModeStatus ? '#242632' : '#ececec',
			  color: darkModeStatus ? 'white' : null,
			  padding: 20,
		  }
		: {
			  height: matches ? null : '300px',
			  width: matches ? '300px' : '450px',
			  flexDirection: 'column',
			  backgroundColor: darkModeStatus ? '#242632' : '#ececec',
			  color: darkModeStatus ? 'white' : null,
		  };

	return (
		<Modal
			aria-labelledby='transition-modal-title'
			aria-describedby='transition-modal-description'
			open={loadalert}
			onClose={(_, reason) => reason === 'backdropClick' && closemodel}
			closeAfterTransition
			slots={{ backdrop: StyledBackdrop }}
			container={container}>
			<Fade in={loadalert}>
				<ModalContent className={effectiveFullScreen ? 'fullScreen' : ''} sx={{}}>
					<div style={contentBoxStyle}>
						<div
							style={{
								height: '80%',
								alignContent: 'center',
								paddingBottom: matches && '10px',
								paddingTop: matches && '10px',
							}}>
							<center>
								<div
									className='mt-2'
									style={{
										height: matches ? '50px' : '70px',
										width: matches ? '50px' : '70px',
										alignContent: 'center',
										justifyItems: 'center',
										borderRadius: '100px',
										border:
											altcolor === 'danger'
												? ' 4px solid rgb(253, 83, 97)'
												: altcolor === 'primary'
												? ' 4px solid #3aba6f'
												: ' 4px solid #ffc107',
									}}>
									<Icon
										icon={
											altcolor === 'danger'
												? 'X'
												: altcolor === 'primary'
												? 'Check'
												: 'Exclamation'
										}
										style={{
											color:
												altcolor === 'danger'
													? 'rgb(253, 83, 97)'
													: altcolor === 'primary'
													? '#3aba6f'
													: '#ffc107',
										}}
										size='5x'
										forceFamily='bootstrap'
									/>
								</div>
							</center>

							<div
								className='mt-4'
								style={{
									fontSize: !matches && '22px',
									fontWeight: 500,
									display: 'flex',
									width: '100%',
									alignItems: 'center',
									flexDirection: 'column',
									justifyContent: 'space-between',
									fontWeight: 'bold',
									textShadow: '0px 3px 12px 0px rgb(0 0 0 / 0.4)',
								}}>
								{
									currentLang === LANG.EN
										? msgtypeloadalert.toLocaleUpperCase()
										: t(msgtypeloadalert) /* i18next-extract-disable-line */
								}
								{/* {msgtypeloadalert.toLocaleUpperCase()} */}
							</div>
							<div
								className='mt-1'
								style={{
									fontSize: !matches && '15px',
									display: 'flex',
									paddingLeft: '25px',
									paddingRight: '25px',
									width: '100%',
									alignItems: 'center',
									flexDirection: 'column',
									justifyContent: 'space-between',
									textShadow: '0px 3px 12px 0px rgb(0 0 0 / 0.4)',
								}}>
								{msgloadalert}
							</div>
							{msgsmtext && (
								<div
									className='mt-1'
									style={{
										fontSize: '11px',
										display: 'flex',
										width: '100%',
										alignItems: 'center',
										flexDirection: 'column',
										justifyContent: 'space-between',
										textShadow: '0px 3px 12px 0px rgb(0 0 0 / 0.4)',
									}}>
									{msgsmtext}
								</div>
							)}
						</div>
						<div
							className='row'
							style={{
								height: '20%',
								alignContent: 'center',
								backgroundColor:
									altcolor === 'danger'
										? 'rgb(253, 83, 97)'
										: altcolor === 'primary'
										? '#3aba6f'
										: '#ffc107',
							}}>
							<div
								className='col text-center h-100 d-flex align-items-center justify-content-center'
								style={{
									backgroundColor:
										altcolor === 'warning' && confirmbt && '#e6e6e6',
								}}>
								<button
									type='button'
									class='btn btn'
									onClick={closemodel}
									data-mdb-ripple-init
									style={{
										height: '100%',
										width: '100%',
										fontSize: !matches && '18px',
										paddingTop: matches && '10px',
										paddingBottom: matches && '10px',
										boxShadow: 'none',
										color:
											altcolor !== 'warning'
												? 'white'
												: altcolor === 'warning' && !confirmbt && 'white',
									}}>
									{t('Close')}
								</button>
							</div>

							{altcolor === 'warning' && confirmbt && (
								<div className='col d-flex align-items-center justify-content-center'>
									<button
										type='button'
										class='btn btn'
										onClick={confirmbt}
										data-mdb-ripple-init
										style={{
											height: '100%',
											width: '100%',
											fontSize: !matches && '18px',
											paddingTop: matches && '10px',
											paddingBottom: matches && '10px',
											boxShadow: 'none',
											color: 'white',
										}}>
										{t('Confirm')}
									</button>
								</div>
							)}
						</div>
					</div>
				</ModalContent>
			</Fade>
		</Modal>
	);
};

const Backdrop = React.forwardRef((props, ref) => {
	const { open, ...other } = props;
	return (
		<Fade in={open}>
			<div ref={ref} {...other} />
		</Fade>
	);
});

Backdrop.propTypes = {
	open: PropTypes.bool,
};

const blue = {
	200: '#99CCFF',
	300: '#66B2FF',
	400: '#3399FF',
	500: '#007FFF',
	600: '#0072E5',
	700: '#0066CC',
};

const grey = {
	50: '#F3F6F9',
	100: '#E5EAF2',
	200: '#DAE2ED',
	300: '#C7D0DD',
	400: '#B0B8C4',
	500: '#9DA8B7',
	600: '#6B7A90',
	700: '#434D5B',
	800: '#303740',
	900: '#1C2025',
};

const Modal = styled(BaseModal)`
	position: fixed;
	z-index: 1300;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const StyledBackdrop = styled(Backdrop)`
	z-index: -1;
	position: fixed;
	inset: 0;
	background-color: rgb(0 0 0 / 0.57);
	-webkit-tap-highlight-color: transparent;
`;

const ModalContent = styled('div')(
	({ theme }) => css`
		text-align: center;
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 8px;
		overflow: hidden;
		border-radius: 20px;
		border: 0px solid transparent;
		outline: none;
		box-shadow: 0 4px 12px
			${theme.palette.mode === 'dark' ? 'rgb(0 0 0 / 0.5)' : 'rgb(0 0 0 / 0.2)'};
		padding: 0px;
		color: ${theme.palette.mode === 'dark' ? grey[50] : grey[900]};

		& .modal-title {
			color: white;
			margin: 0;
			line-height: 1.5rem;
			margin-bottom: 8px;
		}

		& .modal-description {
			margin: 0;
			line-height: 1.5rem;
			font-weight: 400;
			color: ${theme.palette.mode === 'dark' ? grey[400] : grey[800]};
			margin-bottom: 4px;
		}

		&.fullScreen {
			width: 100vw;
			height: 100vh;
			border-radius: 0;
			padding: 0;
		}
	`,
);
